import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useInvoices, useUsage } from '../hooks/useApi';
import { formatCurrency, formatDate, getStatusBadgeClass, storage, todayYMD } from '../lib/utils';
import { 
  FileText, 
  Plus, 
  Users, 
  Package, 
  TrendingUp,
  Download,
  Eye,
  Sparkles,
  PenLine
} from 'lucide-react';

// Check if there's a draft with content
const checkDraft = () => {
  const draft = storage.get('invoiceflow_draft');
  return draft && (draft.customer_name || draft.line_items?.some(item => item.description));
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { getInvoices, downloadPdf, loading: invoicesLoading } = useInvoices();
  const { getUsage, loading: usageLoading } = useUsage();
  
  const [invoices, setInvoices] = useState([]);
  const [usage, setUsage] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    setHasDraft(checkDraft());
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    
    const loadData = async () => {
      const [invoicesRes, usageRes] = await Promise.all([
        getInvoices(),
        getUsage()
      ]);
      
      if (invoicesRes.data) setInvoices(invoicesRes.data);
      if (usageRes.data) setUsage(usageRes.data);
    };
    
    loadData();
  }, []);

  const recentInvoices = invoices.slice(0, 5);
  // Reporting currency is USD: sum the server's rate-snapshot figure
  // (total_usd), never raw totals across currencies. An invoice with
  // no snapshot and a non-USD currency is skipped rather than guessed.
  const usdTotal = (list) => list.reduce((sum, inv) => sum + (inv.total_usd
    ?? (inv.currency === 'USD' ? inv.total : 0)), 0);
  const totalRevenue = usdTotal(invoices.filter(inv => inv.status === 'paid'));
  const sentInvoices = invoices.filter(inv => inv.status === 'sent');
  const uncollected = usdTotal(sentInvoices);
  // When the money is supposed to arrive: USD per due date, ascending.
  // YYYY-MM-DD strings sort correctly as strings.
  const uncollectedByDue = Object.entries(
    sentInvoices.reduce((acc, inv) => {
      const due = inv.due_date || 'no due date';
      acc[due] = (acc[due] || 0) + (inv.total_usd
        ?? (inv.currency === 'USD' ? inv.total : 0));
      return acc;
    }, {})
  ).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Draft Banner */}
      {hasDraft && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <PenLine className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-amber-900">You have an unsaved draft</p>
                  <p className="text-sm text-amber-700">Continue where you left off</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/invoices/new')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="continue-draft-btn"
              >
                Continue Draft
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back! Here's your invoicing overview.</p>
        </div>
        <Button 
          data-testid="create-invoice-btn"
          onClick={() => navigate('/invoices/new')}
          className="gap-2 bg-accent hover:bg-accent/90 text-accent-foreground"
        >
          <Plus className="w-4 h-4" />
          New Invoice
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Usage Card */}
        <Card className="stats-card border-accent/20 bg-gradient-to-br from-accent/5 to-accent/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent" />
              Monthly Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold font-mono">
                {usage?.invoices_this_month || 0}
              </span>
              <span className="text-muted-foreground">/ {usage?.free_tier_limit || 3}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {usage?.remaining || 3} invoices remaining
            </p>
          </CardContent>
        </Card>

        {/* Total Invoices */}
        <Card className="stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Total Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono">
              {usage?.total_invoices || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">All time</p>
          </CardContent>
        </Card>

        {/* Customers */}
        <Card className="stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" />
              Customers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono">
              {usage?.total_customers || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Saved contacts</p>
          </CardContent>
        </Card>

        {/* Revenue */}
        <Card className="stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Paid Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono currency">
              {formatCurrency(totalRevenue)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">From paid invoices</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Uncollected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono currency">
              {formatCurrency(uncollected)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">From sent invoices</p>
            {uncollectedByDue.length > 0 && (
              <div className="mt-2 space-y-0.5">
                {uncollectedByDue.map(([due, amount]) => {
                  const overdue = /^\d{4}-\d{2}-\d{2}$/.test(due) && due < todayYMD();
                  return (
                    <div key={due}
                         className={`flex justify-between text-xs ${overdue ? 'text-red-600' : 'text-muted-foreground'}`}>
                      <span>{/^\d{4}/.test(due) ? formatDate(due) : due}{overdue ? ' · overdue' : ''}</span>
                      <span className="font-mono">{formatCurrency(amount)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Recent Invoices */}
        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Invoices</CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/invoices')}
              data-testid="view-all-invoices-btn"
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {invoicesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner" />
              </div>
            ) : recentInvoices.length === 0 ? (
              <div className="empty-state">
                <FileText className="w-12 h-12 mb-4 opacity-50" />
                <p className="font-medium">No invoices yet</p>
                <p className="text-sm">Create your first invoice to get started</p>
                <Button 
                  className="mt-4 gap-2" 
                  onClick={() => navigate('/invoices/new')}
                  data-testid="create-first-invoice-btn"
                >
                  <Plus className="w-4 h-4" />
                  Create Invoice
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {recentInvoices.map((invoice) => (
                  <div 
                    key={invoice.id}
                    className="flex items-center justify-between p-4 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                    data-testid={`invoice-row-${invoice.invoice_number}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold">
                            #{invoice.invoice_number}
                          </span>
                          <Badge 
                            variant="outline" 
                            className={getStatusBadgeClass(invoice.status)}
                          >
                            {invoice.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {invoice.customer_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-mono font-semibold">
                          {formatCurrency(invoice.total, invoice.currency)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(invoice.issue_date)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/invoices/${invoice.id}`);
                          }}
                          data-testid={`view-invoice-${invoice.invoice_number}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadPdf(invoice.id, invoice.invoice_number);
                          }}
                          data-testid={`download-invoice-${invoice.invoice_number}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3 h-12"
              onClick={() => navigate('/invoices/new')}
              data-testid="quick-new-invoice"
            >
              <Plus className="w-4 h-4" />
              New Invoice
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3 h-12"
              onClick={() => navigate('/customers')}
              data-testid="quick-customers"
            >
              <Users className="w-4 h-4" />
              Manage Customers
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3 h-12"
              onClick={() => navigate('/items')}
              data-testid="quick-items"
            >
              <Package className="w-4 h-4" />
              Manage Items
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
